import { MeteoHomeContext } from 'pages/meteo/[type]/[locality]';
import React, { FC, MouseEvent, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Text } from '@components/Text/Text.component';
import { FullPageLoader } from '@layout/components/FullPageLoader/FullPageLoader.component';
import { PlcAnchor } from '@layout/components/PlcAnchor/PlcAnchor.component';
import { getCityFromUrl } from '@meteo/business/Location.utils';
import { MeteoHomeTabs } from '@meteo/business/Meteo.types';
import { setLastCity } from '@meteo/business/meteo.utils';
import { PlcDispatch } from '@store/store';
import { usePlcRouter } from '@utils/customHooks';
import { urlEnum } from '@utils/url';
import Axios, { AxiosResponse } from 'axios';
import { CityDepartment, VillesProchesProps } from './types';

/**
 * List the matching cities for a given postCode
 * @param postCode
 * @returns The matching cities according to the postCode
 */
async function getCitiesFromCoordinates(postCode: string): Promise<AxiosResponse<Array<CityDepartment>> | undefined> {
  const result = await Axios.get(`/api/department-cities?postCode=${postCode}`);

  if (result.data.length === 0) {
    return undefined;
  }

  return result;
}

/**
 * Build the target URL according to the current MeteoHomeTabs and the current selectedCity.
 */
function buildCityTargetUrl(activeTab: MeteoHomeTabs, selectedCity: CityDepartment) {
  const tabUrl = activeTab.valueOf();
  const locality = `${selectedCity?.postCode}-${selectedCity?.cityName}`;

  const href = `${urlEnum.meteo}/[type]/[locality]`;
  const as = `${urlEnum.meteo}/${tabUrl}/${locality}`;

  return {
    as,
    href,
  };
}

const VillesProches: FC<VillesProchesProps> = ({ activeTab = MeteoHomeTabs.HourByHour }) => {
  const [data, setData] = useState<Array<CityDepartment>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    city: { data: city, setData: setCity },
  } = useContext(MeteoHomeContext);
  const router = usePlcRouter();
  const {
    location: { setCurrentLocation },
  } = useDispatch<PlcDispatch>();

  useEffect(() => {
    getCitiesFromCoordinates(city?.properties.postcode || '')
      .then(result => setData(result?.data || []))
      .catch(error => console.error('Unable to retrieve cities', error));
    return () => {
      setData([]);
    };
  }, [city]);

  function handleCityClick(event: MouseEvent<HTMLAnchorElement>, currentCity: CityDepartment) {
    event.preventDefault();
    setIsLoading(true);
    const targetUrl = buildCityTargetUrl(activeTab, currentCity);
    const locality = `${currentCity?.postCode}-${currentCity?.cityName}`;
    getCityFromUrl(locality)
      .then(result => {
        if (result !== undefined && setCity) {
          setLastCity(result);
          setCity(result);
          setCurrentLocation(result);
        }
      })
      .finally(() => {
        router.push(targetUrl.href, targetUrl.as);
        setIsLoading(false);
      });
  }

  return (
    <section className="wrapper-s ">
      <Text className="plc-mb-m" flavour="black" i18nKey="meteo.boxLinks.title" tag="p" variant="new-h3" />
      <div className="new-grid-m-2-s-1">
        {data &&
          data.map(r => (
            // eslint-disable-next-line react/jsx-no-bind
            <PlcAnchor
              key={r.cityIndex}
              linkProps={buildCityTargetUrl(activeTab, r)}
              onClick={(e: MouseEvent<HTMLAnchorElement>) => handleCityClick(e, r)}
            >
              <Text tag="span" variant="small">
                {r.cityName}
              </Text>
            </PlcAnchor>
          ))}
      </div>
      <FullPageLoader visible={isLoading} />
    </section>
  );
};

export default VillesProches;
